import { Outlet, Link, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { usePortalStore } from '../../contexts/portalStore';
import { initializePortalTheme } from '@pp/shared/theme/portalThemeStore';
import { portalApi, type PortalFeatures, type PortalTenantBranding } from '../../api/portalApi';
import {
  User,
  FileText,
  CreditCard,
  Settings,
  LogOut,
  Home,
  Menu,
  X,
  HelpCircle,
  Wifi,
  Ticket,
  ChevronDown,
} from 'lucide-react';
import { useState, useEffect, useMemo, useRef } from 'react';
import { PortalThemeToggle } from '../portal/PortalThemeToggle';

const DEFAULT_ACCENT = '#2563eb';

/** "Ridgeline Fiber" -> "RF", "Maria Delgado" -> "MD", "Acme" -> "A" */
function initialsOf(name: string | undefined, fallback: string): string {
  const parts = (name ?? '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return fallback;
  return parts
    .slice(0, 2)
    .map((p) => p[0]!.toUpperCase())
    .join('');
}

export function PortalLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, account } = usePortalStore();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [features, setFeatures] = useState<PortalFeatures | null>(null);
  // Branding is keyed by subdomain so a stale tenant's logo never shows after a tenant switch
  const [brandingState, setBrandingState] = useState<{
    subdomain: string;
    data: PortalTenantBranding | null;
  } | null>(null);
  const userMenuRef = useRef<HTMLDivElement>(null);

  // Initialize theme on mount and listen for system preference changes
  useEffect(() => {
    const cleanup = initializePortalTheme();
    return cleanup;
  }, []);

  // Fetch portal features when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      portalApi.getPortalFeatures()
        .then(setFeatures)
        .catch(() => {
          // Default to showing nothing if fetch fails
          setFeatures({ ticketsEnabled: false, faqEnabled: false });
        });
    }
  }, [isAuthenticated]);

  // Tenant branding (logo + accent colour) once we know which tenant this is
  const tenantSubdomain = account?.tenantSubdomain;
  useEffect(() => {
    if (!tenantSubdomain) return;
    let cancelled = false;
    portalApi.getTenantBranding(tenantSubdomain).then((data) => {
      if (!cancelled) setBrandingState({ subdomain: tenantSubdomain, data });
    });
    return () => {
      cancelled = true;
    };
  }, [tenantSubdomain]);
  const branding =
    tenantSubdomain && brandingState?.subdomain === tenantSubdomain ? brandingState.data : null;

  // A signed-in customer landing on the login page goes straight to their dashboard
  useEffect(() => {
    if (
      isAuthenticated &&
      account &&
      (location.pathname === '/portal' || location.pathname === '/portal/login')
    ) {
      navigate('/portal/dashboard', { replace: true });
    }
  }, [isAuthenticated, account, location.pathname, navigate]);

  // Close the user menu on outside click or Escape
  useEffect(() => {
    if (!userMenuOpen) return;
    const onPointerDown = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setUserMenuOpen(false);
      }
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setUserMenuOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [userMenuOpen]);

  const handleLogout = async () => {
    await portalApi.logout();
    navigate('/portal');
  };

  const navItems = useMemo(() => {
    const items = [
      { path: '/portal/dashboard', label: 'Dashboard', icon: Home },
      { path: '/portal/invoices', label: 'Invoices', icon: FileText },
      { path: '/portal/payments', label: 'Payments', icon: CreditCard },
      { path: '/portal/account', label: 'Account', icon: User },
      { path: '/portal/my-plan', label: 'My Plan', icon: Wifi },
      { path: '/portal/autopay', label: 'Autopay', icon: Settings },
    ];

    // Only show Support if tickets are enabled
    if (features?.ticketsEnabled) {
      items.push({ path: '/portal/tickets', label: 'Support', icon: Ticket });
    }

    // Only show FAQ if tenant has published FAQs
    if (features?.faqEnabled) {
      items.push({ path: '/portal/faqs', label: 'FAQ', icon: HelpCircle });
    }

    return items;
  }, [features]);

  const tenantName = branding?.tenantName || account?.tenantName || 'Customer Portal';
  const portalName = branding?.portalAppName || 'Customer Portal';
  const accent = branding?.primaryColor || DEFAULT_ACCENT;
  const logoUrl = branding?.logoUrl || branding?.portalAppIconUrl || null;
  const customerName = account?.customerName || 'Your account';
  const customerInitials = initialsOf(account?.customerName, 'C');

  const navLinkClass = ({ isActive }: { isActive: boolean }) =>
    `flex items-center gap-1.5 whitespace-nowrap px-3 py-2 text-sm font-medium rounded-md transition-colors ${
      isActive
        ? 'bg-gray-100 text-gray-900 dark:bg-gray-700 dark:text-white'
        : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-300 dark:hover:bg-gray-700 dark:hover:text-white'
    }`;

  const mobileNavLinkClass = ({ isActive }: { isActive: boolean }) =>
    `flex items-center gap-3 px-3 py-2.5 text-sm font-medium rounded-md ${
      isActive
        ? 'bg-gray-100 text-gray-900 dark:bg-gray-700 dark:text-white'
        : 'text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700'
    }`;

  const brand = (
    <Link
      to={isAuthenticated ? '/portal/dashboard' : '/portal'}
      className="flex min-w-0 items-center gap-3"
      aria-label={`${tenantName} customer portal home`}
    >
      {logoUrl ? (
        <img src={logoUrl} alt="" className="h-8 w-auto max-w-[160px] object-contain" />
      ) : (
        <span
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-sm font-bold text-white"
          style={{ backgroundColor: accent }}
          aria-hidden="true"
        >
          {initialsOf(tenantName, 'P')}
        </span>
      )}
      <span className="flex min-w-0 flex-col leading-tight">
        <span className="truncate text-base font-semibold text-gray-900 dark:text-white">
          {tenantName}
        </span>
        <span className="hidden text-xs text-gray-500 dark:text-gray-400 sm:block">
          {portalName}
        </span>
      </span>
    </Link>
  );

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <header className="sticky top-0 z-50 border-b border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex h-16 items-center justify-between gap-4">
            {brand}

            {isAuthenticated && (
              <>
                {/* Desktop Navigation */}
                <nav className="hidden items-center gap-1 md:flex" aria-label="Portal">
                  {navItems.map((item) => (
                    <NavLink
                      key={item.path}
                      to={item.path}
                      className={navLinkClass}
                      onClick={() => setUserMenuOpen(false)}
                    >
                      <item.icon className="hidden h-4 w-4 lg:block" aria-hidden="true" />
                      {item.label}
                    </NavLink>
                  ))}
                </nav>

                {/* User menu (desktop) */}
                <div className="relative hidden md:block" ref={userMenuRef}>
                  <button
                    type="button"
                    onClick={() => setUserMenuOpen((open) => !open)}
                    className="flex items-center gap-2 rounded-full py-1 pl-1 pr-2 text-sm font-medium text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-700"
                    aria-haspopup="menu"
                    aria-expanded={userMenuOpen}
                    aria-label="Account menu"
                  >
                    <span
                      className="flex h-8 w-8 items-center justify-center rounded-full text-xs font-semibold text-white"
                      style={{ backgroundColor: accent }}
                      aria-hidden="true"
                    >
                      {customerInitials}
                    </span>
                    <span className="hidden max-w-[140px] truncate lg:inline">{customerName}</span>
                    <ChevronDown className="h-4 w-4 text-gray-400" aria-hidden="true" />
                  </button>

                  {userMenuOpen && (
                    <div
                      role="menu"
                      className="absolute right-0 mt-2 w-64 overflow-hidden rounded-xl border border-gray-200 bg-white shadow-lg dark:border-gray-700 dark:bg-gray-800"
                    >
                      <div className="border-b border-gray-100 px-4 py-3 dark:border-gray-700">
                        <p className="truncate text-sm font-semibold text-gray-900 dark:text-white">
                          {customerName}
                        </p>
                        <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                          Account {account?.accountNumber}
                        </p>
                      </div>
                      <div className="border-b border-gray-100 px-4 py-3 dark:border-gray-700">
                        <p className="mb-2 text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                          Appearance
                        </p>
                        <PortalThemeToggle />
                      </div>
                      <button
                        type="button"
                        role="menuitem"
                        onClick={handleLogout}
                        className="flex w-full items-center gap-2 px-4 py-3 text-left text-sm font-medium text-gray-700 hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-gray-700"
                      >
                        <LogOut className="h-4 w-4" aria-hidden="true" />
                        Sign out
                      </button>
                    </div>
                  )}
                </div>

                {/* Mobile menu button */}
                <div className="flex items-center md:hidden">
                  <button
                    type="button"
                    onClick={() => setMobileMenuOpen((open) => !open)}
                    className="rounded-lg p-2 text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
                    aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
                    aria-expanded={mobileMenuOpen}
                  >
                    {mobileMenuOpen ? (
                      <X className="h-6 w-6" aria-hidden="true" />
                    ) : (
                      <Menu className="h-6 w-6" aria-hidden="true" />
                    )}
                  </button>
                </div>
              </>
            )}

            {!isAuthenticated && (
              <div className="flex items-center gap-4">
                <PortalThemeToggle />
              </div>
            )}
          </div>
        </div>

        {/* Mobile Navigation */}
        {isAuthenticated && mobileMenuOpen && (
          <div className="border-t border-gray-200 md:hidden dark:border-gray-700">
            <div className="flex items-center gap-3 px-4 py-3">
              <span
                className="flex h-9 w-9 items-center justify-center rounded-full text-xs font-semibold text-white"
                style={{ backgroundColor: accent }}
                aria-hidden="true"
              >
                {customerInitials}
              </span>
              <div className="min-w-0">
                <p className="truncate text-sm font-semibold text-gray-900 dark:text-white">
                  {customerName}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Account {account?.accountNumber}
                </p>
              </div>
            </div>
            <nav className="space-y-1 border-t border-gray-100 px-4 py-2 dark:border-gray-700" aria-label="Portal">
              {navItems.map((item) => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  className={mobileNavLinkClass}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <item.icon className="h-4 w-4" aria-hidden="true" />
                  {item.label}
                </NavLink>
              ))}
            </nav>
            <div className="border-t border-gray-100 px-4 py-3 dark:border-gray-700">
              <p className="mb-2 text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                Appearance
              </p>
              <PortalThemeToggle />
            </div>
            <div className="border-t border-gray-100 px-4 py-2 dark:border-gray-700">
              <button
                type="button"
                onClick={handleLogout}
                className="flex w-full items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-700"
              >
                <LogOut className="h-4 w-4" aria-hidden="true" />
                Sign out
              </button>
            </div>
          </div>
        )}
      </header>

      {/* Main Content */}
      <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        <Outlet />
      </main>

      {/* Footer */}
      <footer className="mt-auto border-t border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800">
        <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
          <div className="text-center text-sm text-gray-500 dark:text-gray-400">
            <p>
              {tenantName} &copy; {new Date().getFullYear()}
            </p>
            <p className="mt-1">
              Need help?{' '}
              <a
                href="mailto:support@packetprospector.com"
                className="text-blue-600 hover:underline dark:text-blue-400"
              >
                Contact Support
              </a>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default PortalLayout;
